import { useState } from "react";
import type { LayoutChangeEvent } from "react-native";
import type { FontSchemaType } from "../../font";
import Box from "../Box";
import { CircularGaugeGrid } from "./charts/CircularGaugeGrid";
import { CircularProgressList } from "./charts/CircularProgressList";
import { DailyStreakChart } from "./charts/DailyStreakChart";
import { PerformanceComparisonChart } from "./charts/PerformanceComparisonChart";
import { SkillRadarChart } from "./charts/SkillRadarChart";
import { TestReadinessLineChart } from "./charts/TestReadinessLineChart";
import { TimeDistributionBarChart } from "./charts/TimeDistributionBarChart";
import { SPACING } from "./constants";
import type {
	BarChartData,
	ChartType,
	CircularChartData,
	CircularGaugeGridData,
	CircularProgressListData,
	ComparisonChartData,
	LineChartData,
	RadarChartData,
} from "./types";

export type ChartProps =
	| { chartType: "radar"; chartData: RadarChartData }
	| { chartType: "line"; chartData: LineChartData }
	| { chartType: "bar"; chartData: BarChartData; threshold?: number }
	| { chartType: "circular"; chartData: CircularChartData }
	| { chartType: "comparison"; chartData: ComparisonChartData }
	| { chartType: "circularProgressList"; chartData: CircularProgressListData }
	| { chartType: "circularGaugeGrid"; chartData: CircularGaugeGridData };

export type CardContentProps = ChartProps & {
	theme?: { font?: FontSchemaType };
	customChartComponent?: React.ReactNode;
};

export function buildChartProps(
	chartType: ChartType,
	chartData: unknown,
	threshold?: number,
): ChartProps {
	switch (chartType) {
		case "radar":
			return { chartType, chartData } as ChartProps;
		case "line":
			return { chartType, chartData } as ChartProps;
		case "bar":
			return { chartType, chartData, threshold } as ChartProps;
		case "circular":
			return { chartType, chartData } as ChartProps;
		case "comparison":
			return { chartType, chartData } as ChartProps;
		case "circularProgressList":
			return { chartType, chartData } as ChartProps;
		case "circularGaugeGrid":
			return { chartType, chartData } as ChartProps;
		default:
			throw new Error(`[DataCard] Unknown chartType: "${String(chartType)}"`);
	}
}

interface ChartRenderContext {
	containerWidth: number;
	appFont?: FontSchemaType;
}

function renderRadarChart(data: RadarChartData, ctx: ChartRenderContext) {
	if (ctx.containerWidth <= 0) {
		return null;
	}
	return (
		<Box style={{ width: "100%" }} alignItems="center" justifyContent="center">
			<SkillRadarChart
				data={data}
				appFont={ctx.appFont}
				containerWidth={ctx.containerWidth}
			/>
		</Box>
	);
}

function renderLineChart(data: LineChartData, ctx: ChartRenderContext) {
	if (ctx.containerWidth <= 0) {
		return null;
	}
	return (
		<Box style={{ width: "100%" }}>
			<TestReadinessLineChart
				data={data}
				appFont={ctx.appFont}
				containerWidth={ctx.containerWidth}
			/>
		</Box>
	);
}

function renderBarChart(
	data: BarChartData,
	ctx: ChartRenderContext,
	threshold?: number,
) {
	if (ctx.containerWidth <= 0) {
		return null;
	}
	return (
		<Box style={{ width: "100%" }}>
			<TimeDistributionBarChart
				data={data}
				threshold={threshold}
				containerWidth={ctx.containerWidth}
			/>
		</Box>
	);
}

function renderCircularChart(data: CircularChartData) {
	return (
		<Box style={{ width: "100%" }}>
			<DailyStreakChart
				weekData={data.weekData}
				currentStreak={data.currentStreak}
				bestStreak={data.bestStreak}
			/>
		</Box>
	);
}

function renderComparisonChart(
	data: ComparisonChartData,
	ctx: ChartRenderContext,
) {
	if (ctx.containerWidth <= 0) {
		return null;
	}
	return (
		<Box style={{ width: "100%" }}>
			<PerformanceComparisonChart
				data={data}
				appFont={ctx.appFont}
				containerWidth={ctx.containerWidth}
			/>
		</Box>
	);
}

function renderCircularProgressList(data: CircularProgressListData) {
	return (
		<Box style={{ width: "100%" }}>
			<CircularProgressList items={data} />
		</Box>
	);
}

function renderCircularGaugeGrid(data: CircularGaugeGridData) {
	return (
		<Box style={{ width: "100%" }}>
			<CircularGaugeGrid gauges={data} />
		</Box>
	);
}

const VALID_CHART_TYPES: ReadonlySet<ChartType> = new Set([
	"radar",
	"line",
	"bar",
	"circular",
	"comparison",
	"circularProgressList",
	"circularGaugeGrid",
]);

function renderChartByType(
	props: ChartProps,
	ctx: ChartRenderContext,
): React.ReactNode {
	if (!VALID_CHART_TYPES.has(props.chartType)) {
		return null;
	}

	switch (props.chartType) {
		case "radar":
			return renderRadarChart(props.chartData, ctx);
		case "line":
			return renderLineChart(props.chartData, ctx);
		case "bar":
			return renderBarChart(props.chartData, ctx, props.threshold);
		case "circular":
			return renderCircularChart(props.chartData);
		case "comparison":
			return renderComparisonChart(props.chartData, ctx);
		case "circularProgressList":
			return renderCircularProgressList(props.chartData);
		case "circularGaugeGrid":
			return renderCircularGaugeGrid(props.chartData);
		default:
			return null;
	}
}

export const CardContent = (props: CardContentProps) => {
	const { theme, customChartComponent } = props;
	const [containerWidth, setContainerWidth] = useState(0);

	const handleLayout = (event: LayoutChangeEvent) => {
		const { width } = event.nativeEvent.layout;
		if (width > 0 && width !== containerWidth) {
			setContainerWidth(width);
		}
	};

	if (customChartComponent) {
		return (
			<Box padding={SPACING.card} style={{ width: "100%" }}>
				{customChartComponent}
			</Box>
		);
	}

	const ctx: ChartRenderContext = {
		containerWidth,
		appFont: theme?.font,
	};

	return (
		<Box alignItems="center" style={{ width: "100%" }} onLayout={handleLayout}>
			{renderChartByType(props, ctx)}
		</Box>
	);
};
